import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { IHsnSacCode } from "./HSNCodeViewController";
import { Formik, Form, FormikProvider, FieldArray, ErrorMessage } from "formik";
import {
  fetchHsnSacCodeById,
  handleSubmitHsnSacCode,
  HSN_SAC_CODE_FIELD_INITIAL_VALUES,
  hsnSacCodeFieldValidationSchema,
  IHsnSacCodeForm,
} from "./HSNCodeFormController";
import {
  getLocalDate,
  handleError,
  hasAnyModification,
  sweetAlertError,
} from "../../utils/AppFunctions";
import { MESSAGE_UNKNOWN_ERROR_OCCURRED } from "../../utils/AppConstants";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
} from "../../core-component/ui";
import Loader from "../../core-component/ui/Loader";
import {
  ICFormikDatePicker,
  ICFormikSwitch,
  ICFormikTextInput,
} from "../../core-component/formik";
import ICFormikTextArea from "../../core-component/formik/ICFormikTextIArea";
import { BIT_VALUE, DATE_FORMAT } from "../../utils/AppEnumerations";
import { deleteIcon } from "../../assets/icons/SvgIconList";
import { cn } from "../../lib";
import { ICTextInput } from "../../core-component/form";

interface IHsnSacCodeFormProps {
  handleClose: (status: boolean) => void;
  readOnly: boolean;
  hsnSacCodeToEdit?: IHsnSacCode;
}
export const RatePercentage = ({
  values,
  formikProps,
  index,
  arrayHelpers,
  readOnly,
}: {
  values: IHsnSacCodeForm;
  formikProps: any;
  index: number;
  arrayHelpers: any;
  readOnly: boolean;
}) => {
  const { t } = UseTranslationHook();
  const handleRatePercentageClick = (
    values: IHsnSacCodeForm,
    index: number,
    arrayHelpers: any
  ) => {
    if (shouldRemoveItem(values.rate_percentage.length)) {
      arrayHelpers.remove(index);
    }
  };

  const shouldRemoveItem = (ratePercentageLength: number) => {
    return ratePercentageLength > 1;
  };
  return (
    <div className="w-full flex flex-col ">
      <div className="flex items-center gap-4">
        <div className="flex flex-col w-full">
          <ICTextInput
            type="number"
            label={t("rate_percentage")}
            name={`rate_percentage[${index}]`}
            value={formikProps.values.rate_percentage[index] ?? ""}
            onChange={formikProps.handleChange}
            disabled={readOnly}
            containerClassName="mt-4"
          />
          <ErrorMessage
            name={`rate_percentage[${index}]`}
            component="div"
            className="text-danger mt-1"
          />
        </div>
        {!readOnly && (
          <div
            onClick={() =>
              handleRatePercentageClick(values, index, arrayHelpers)
            }
            className={cn("ml-2 h-10 flex items-center", {
              "cursor-pointer": values.rate_percentage.length > 1,
            })}
          >
            <div className="size-5 mt-4">
              {values.rate_percentage.length > 1 && deleteIcon}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const RenderRatePercentage = ({
  values,
  formikProps,
  readOnly,
}: {
  values: IHsnSacCodeForm;
  formikProps: any;
  readOnly: boolean;
}) => {
  const { t } = UseTranslationHook();
  return (
    <FieldArray
      name="rate_percentage"
      render={(arrayHelpers) => (
        <div className="w-full">
          <p>{t("rate_percentage")}</p>
          {values?.rate_percentage.map((item, index) => (
            <RatePercentage
              key={index}
              index={index}
              arrayHelpers={arrayHelpers}
              values={values}
              formikProps={formikProps}
              readOnly={readOnly}
            />
          ))}
          {!readOnly && (
            <div className="mt-4">
              <ICButton
                type="button"
                onClick={() => {
                  arrayHelpers.push(null);
                }}
              >
                {t("add_rate_percentage")}
              </ICButton>
            </div>
          )}
        </div>
      )}
    />
  );
};

const HsnSacCodeForm: React.FC<IHsnSacCodeFormProps> = ({
  handleClose,
  hsnSacCodeToEdit,
  readOnly,
}) => {
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [hsnSacCodeFormInitialValues, setHsnSacCodeFormInitialValues] =
    useState<IHsnSacCodeForm>();

  useEffect(() => {
    if (hsnSacCodeToEdit) {
      getHsnSacCodeById(hsnSacCodeToEdit.id);
    } else {
      setHsnSacCodeFormInitialValues(HSN_SAC_CODE_FIELD_INITIAL_VALUES);
    }
  }, []);

  const getHsnSacCodeById = async (id: string) => {
    setLoading(true);
    await fetchHsnSacCodeById(id, handleClose, setHsnSacCodeFormInitialValues);
    setLoading(false);
  };

  const handleSubmit = async (values: IHsnSacCodeForm) => {
    try {
      const duplicates = values.rate_percentage.filter(
        (item, index) => values.rate_percentage.indexOf(item) !== index
      );
      if (duplicates && duplicates.length > 0) {
        sweetAlertError({
          message: t("rate_percentage_duplicate_massage"),
        });
        return;
      }

      setSubmitLoading(true);
      await handleSubmitHsnSacCode(
        values,
        setSubmitLoading,
        hsnSacCodeToEdit?.id,
        handleClose
      );
    } catch (e: any) {
      sweetAlertError({ message: MESSAGE_UNKNOWN_ERROR_OCCURRED });
      handleError(e, "hsnSacCodeSubmit");
    } finally {
      setSubmitLoading(false);
    }
  };

  const checkFormModification = (values: IHsnSacCodeForm) => {
    if (
      hsnSacCodeFormInitialValues &&
      hasAnyModification(values, hsnSacCodeFormInitialValues)
    ) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          {hsnSacCodeFormInitialValues && (
            <Formik
              initialValues={hsnSacCodeFormInitialValues}
              validationSchema={hsnSacCodeFieldValidationSchema(t)}
              onSubmit={(values) => {
                handleSubmit(values);
              }}
            >
              {(formikProps) => {
                const { handleSubmit, values } = formikProps;

                return (
                  <FormRightModalWrapper
                    containerClassName="!w-1/3"
                    onClose={() => checkFormModification(values)}
                    title={t("hsn_sac_code")}
                  >
                    <Form
                      onSubmit={handleSubmit}
                      className="flex flex-col h-full"
                    >
                      <div className="flex-1 overflow-y-scroll pt-2.5 mb-4 space-y-4">
                        <ICFormikTextInput
                          {...formikProps}
                          fieldName={"hsn_sac_code"}
                          placeholder={t("hsn_sac_code")}
                          required
                          tabIndex={0}
                          disabled={readOnly}
                        />
                        <ICFormikTextInput
                          {...formikProps}
                          fieldName={"chapter"}
                          placeholder={t("chapter")}
                          required
                          tabIndex={0}
                          disabled={readOnly}
                        />
                        <ICFormikTextArea
                          {...formikProps}
                          name={"description"}
                          label={t("description")}
                          required
                          disabled={readOnly}
                        />
                        <FormikProvider value={formikProps}>
                          <RenderRatePercentage
                            values={values}
                            formikProps={formikProps}
                            readOnly={readOnly}
                          />
                        </FormikProvider>

                        <ICFormikDatePicker
                          {...formikProps}
                          fieldName={"effective_from"}
                          placeholder={t("effective_from")}
                          dateProps={{
                            displayFormat: DATE_FORMAT["DD-Mon-YYYY"],
                            min: getLocalDate(),
                          }}
                          disabled={readOnly}
                          required
                        />
                        {hsnSacCodeFormInitialValues.id && (
                          <ICFormikDatePicker
                            {...formikProps}
                            fieldName={"end_date"}
                            placeholder={t("end_date")}
                            dateProps={{
                              displayFormat: DATE_FORMAT["DD-Mon-YYYY"],
                              min: getLocalDate(),
                            }}
                            disabled={readOnly}
                          />
                        )}
                        {hsnSacCodeFormInitialValues.id && (
                          <ICFormikSwitch
                            {...formikProps}
                            name={"is_active"}
                            containerClassName="gap-4 !w-fit"
                            placeholder={t("status")}
                            checkedValue={BIT_VALUE.One}
                            unCheckedValue={BIT_VALUE.Zero}
                            disabled={readOnly}
                          />
                        )}
                      </div>

                      {!readOnly && (
                        <ICButton
                          loading={submitLoading}
                          className={"w-full"}
                          children={t("submit")}
                        />
                      )}
                    </Form>
                  </FormRightModalWrapper>
                );
              }}
            </Formik>
          )}
          {showDiscardWarning && (
            <ICConfirmationModal
              title={t("discard_warning")}
              message={t("discard_unsaved_changes")}
              onClose={(value) => {
                setShowDiscardWarning(false);
                value && handleClose(false);
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default HsnSacCodeForm;
